// No need for mime import since we're using direct upload
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '@Helpers';
import { cn } from '@Utils';

import { sendCustomEvent, sendErrorEvent } from '@Analytics';
import { Column, Table } from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { Gs1ConfigurationLink } from '@Models';
import { bopsApi } from '@Network';

const ConfigurationGs1List = ({
  configures,
  setLoading,
  refetch,
  readOnlyMasters = false,
}: {
  configures: Gs1ConfigurationLink[];
  setLoading: (loading: boolean) => void;
  refetch: () => void;
  readOnlyMasters?: boolean;
}) => {
  const { t } = useTranslation();

  // Direct upload is now handled by the API endpoint

  const uploadFile = (input: React.ChangeEvent<HTMLInputElement>, configurationLink: Gs1ConfigurationLink): void => {
    setLoading(true);

    const uploadAsync = async (): Promise<void> => {
      const file = input?.target?.files?.[0];
      if (!file) {
        showErrorToast(t('Configuration.NoFileSelected'));
        setLoading(false);
        return;
      }

      try {
        // Use the new direct upload endpoint that will also trigger the notebook
        const uploadResponse = await bopsApi.uploadLocationMaster(file, 'chatbot');

        if (!uploadResponse || !uploadResponse.data) {
          throw new Error('Failed to upload file');
        }

        sendCustomEvent('GS1_CONFIGURATION_FILE_UPLOADED', {
          type: 'UPLOAD',
          title: configurationLink.title,
        });

        showSuccessToast(t('Configuration.Success'));
        refetch();
      } catch (e) {
        sendErrorEvent(`unable to upload file. ${e instanceof Error ? e.message : String(e)}`);
        throw new Error(`Invalid response`);
      } finally {
        setLoading(false);
      }
    };

    uploadAsync().catch((e) => {
      sendErrorEvent(`unable to request upload file. ${e instanceof Error ? e.message : String(e)}`);
      setLoading(false);
      showErrorToast(t('Configuration.Error'));
    });
  };

  const downloadLatestFile = async (configurationLink: Gs1ConfigurationLink) => {
    try {
      setLoading(true);

      // Use the new direct download endpoint
      const response = await bopsApi.downloadChatbotLocationMaster();

      // Ensure response.data is treated as ArrayBuffer for Excel
      const responseData = response.data as ArrayBuffer;

      // Create a blob URL for downloading
      const blob = new Blob([responseData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);

      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'] as string;
      let fileName = 'chatbot_location_master.xlsx'; // Default filename
      if (contentDisposition) {
        const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (filenameMatch != null && filenameMatch[1]) {
          fileName = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Ensure the filename has .xlsx extension
      if (!fileName.toLowerCase().endsWith('.xlsx')) {
        fileName = `${fileName.replace(/\.[^/.]+$/, '')}.xlsx`;
      }

      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(url);

      // Type assertion for analytics event
      const titleValue: string = configurationLink.title || '';
      sendCustomEvent('GS1_CONFIGURATION_FILE_DOWNLOADED', {
        title: titleValue,
      });

      setLoading(false);
    } catch (error) {
      sendErrorEvent(`unable to download file: ${error instanceof Error ? error.message : String(error)}`);
      setLoading(false);
      showErrorToast(t('Configuration.DownloadError'));
    }
  };

  const builderCell = (
    key: string,
    type: string,
    accessor: string,
    configurationLink: Gs1ConfigurationLink,
  ): JSX.Element => {
    const value = key in configurationLink ? configurationLink[key] : '';

    if (type === 'label') {
      return <span className="font-semibold">{value}</span>;
    }

    if (type === 'buttons') {
      const title = configurationLink.title || '';
      return (
        <div className="flex gap-2">
          <label
            htmlFor={title}
            className={cn(
              'flex items-center justify-center px-3 rounded-md m-auto mr-2 text-sm font-semibold h-10 w-auto shadow-md',
              'border-solid border-gray-300 text-gray-700 hover:border-bg-gray-300 hover:bg-gray-50',
              readOnlyMasters ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:shadow-sm',
            )}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-[15px] mr-[5px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {t('Configuration.Upload')}
            <input
              id={title}
              type="file"
              className="hidden"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv"
              onChange={(e) => uploadFile(e, configurationLink)}
              disabled={readOnlyMasters}
            />
          </label>
          <TrackedButton
            buttonName="GS1_CONFIGURATION_FILE_DOWNLOAD"
            onClick={() => downloadLatestFile(configurationLink)}
            isSubmit={false}
            className="w-auto">
            <div className="flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[15px] mr-[5px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {t('Configuration.Download')}
            </div>
          </TrackedButton>
        </div>
      );
    }
    return <div key={accessor}>{value}</div>;
  };

  const getColumns = (): Column[] => {
    const columns = [
      {
        label: t('Configuration.Name'),
        accessor: 'title',
        key: 'title',
        type: 'label',
        width: 'w-[40%]',
      },
      {
        label: t('Configuration.LastUpdated'),
        accessor: 'last_update',
        key: 'last_update',
        type: 'general',
        width: 'w-[30%]',
      },
      {
        label: '',
        type: 'buttons',
        accessor: 'action',
        key: 'id',
        width: 'w-[30%]',
      },
    ];

    const headers = columns.map((column) => {
      return {
        Header: column.label,
        accessor: column.accessor,
        Cell: ({ row }) => {
          return builderCell(column.key, column.type, column.accessor, row.original);
        },
        width: column.width,
      };
    });
    return headers;
  };

  return (
    <div className="flex mt-[10px]">
      <Table columns={getColumns()} data={configures} pageSize={9} pagination />
    </div>
  );
};

export default ConfigurationGs1List;
